<template>
    <!-- Loading Video -->
    <Transition name="fade">
        <div
            class="bg-bg absolute top-0 left-0 z-10 h-full w-full"
            v-if="!isGameReady"
            data-testid="camelrace-loading"
        >
            <video
                ref="loadingVideo"
                class="absolute top-0 h-full w-full object-cover"
                playsinline="true"
                autoplay
                preload="auto"
                :src="introVideoSrc"
                muted
                @ended="onLoadingVideoEnded"
            />
        </div>
    </Transition>

    <!-- Main Game -->
    <div class="relative h-full w-full">
        <video
            v-if="camelraceAssets.getBlobUrl('backgroundLoop')"
            class="absolute top-0 z-2 h-full w-auto max-w-none -translate-x-1/2 object-cover left-1/2"
            playsinline="true"
            autoplay
            loop
            muted
            preload="auto"
        >
            <source
                :src="camelraceAssets.getBlobUrl('backgroundLoop')"
                :type="camelraceAssets.getEncoding('backgroundLoop')"
            />
        </video>

        <img src="@/assets/images/games/camelrace/left_disabled.webp" class="absolute top-0 left-1/2 -translate-x-1/2 z-2 h-full object-cover" v-if="camels.length < 3" />
        <img src="@/assets/images/games/camelrace/right_disabled.webp" class="absolute top-0 left-1/2 -translate-x-1/2 z-2 h-full object-cover" v-if="camels.length < 4" />

        <video
            v-if="camelraceAssets.getBlobUrl('gates')"
            class="absolute top-0 z-2 h-full w-auto max-w-none -translate-x-1/2 object-cover left-1/2"
            playsinline="true"
            muted
            preload="auto"
            ref="gates"
        >
            <source
                :src="camelraceAssets.getBlobUrl('gates')"
                :type="camelraceAssets.getEncoding('gates')"
            />
        </video>

        <template v-if="camelraceAssets.isReady">
            <CamelVideo
                v-for="camel in camels"
                :key="camel.color"
                :camel="camel"
                @warmed="onCamelWarmed"
            />
        </template>


        <CamelTurnIndicator :profile="currentPlayer" v-if="isGameReady && currentPlayer" />

        <!-- <CamelTurnIndicator /> -->

        <div class="absolute top-5 left-5 z-3 flex flex-col gap-1">
            <CamelBackButton
                @click="showCloseModal = true"
                :class="{ 'pointer-events-none opacity-50': isQuitting }"
            />
            <CamelInfoButton />
        </div>

        <div class="absolute bottom-0 z-3 flex w-full flex-col gap-3 lg:gap-6 p-5 max-w-[53vh] left-1/2 -translate-x-1/2">
            <div class="grid grid-cols-4 grid-rows-1 gap-3 lg:gap-8" v-if="!isTeamPlay">
                <div
                    class="flex"
                    v-for="camel in camels"
                    :style="{ gridColumn: camel.column + 1, gridRow: 1 }"
                    :key="camel.color"
                >
                    <div
                        @click="handleCamelClick(camel)"
                        @click.right.prevent="handleCamelRightClick(camel)"
                        data-testid="camelrace-camel"
                        :data-current="camel.isCurrentPlayersTurn"
                        class="transition-300 relative mx-auto h-27 lg:h-42 w-full rounded-xl"
                        :class="{
                            'pointer-events-none':
                                !camel.isCurrentPlayersTurn && !isCamelClickable(camel),
                            'scale-115': camel.isCurrentPlayersTurn && isCamelClickable(camel),
                            'border-4 shadow-[0px_0px_12.92px_0px_#000000BA]':
                                camel.isCurrentPlayersTurn,
                        }"
                        :style="{
                            backgroundColor: colorsDark[camel.color],
                            borderColor: colorsDark[camel.color],
                        }"
                    >
                        <div
                            class="pointer-events-none absolute top-0 flex h-full w-full flex-col justify-between"
                        >
                            <div
                                class="relative -mb-1 aspect-square w-full flex-1 overflow-hidden p-[0.15rem] pb-0"
                            >
                                <img
                                    v-avatar-fallback="camel.profile.avatar"
                                    :alt="$t('general.avatar_alt')"
                                    class="h-full w-full overflow-hidden rounded-lg rounded-b-none object-cover object-top"
                                    :class="!camel.isCurrentPlayersTurn ? 'opacity-50' : ''"
                                    :style="{
                                        backgroundColor: colors[camel.color],
                                    }"
                                />
                                <svg
                                    viewBox="0 0 64 72"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                    class="absolute top-[0.35rem] -left-0.5 h-full w-full"
                                >
                                    <path
                                        d="M58.8633 71.1886C58.7337 71.8657 61.8259 71.6665 62.4743 71.2384C63.1127 70.8003 63.9406 68.8289 63.2125 68.5202C62.8833 68.3808 59.003 70.5016 58.8633 71.1886Z"
                                        fill="white"
                                    />
                                    <path
                                        d="M5.86676 0.338716C5.97649 -0.228812 3.4129 0.0300603 2.81439 0.298889C0.829354 1.18503 1.56751 2.34 2.18596 2.59887C2.46526 2.71835 5.74706 0.9162 5.86676 0.338716Z"
                                        fill="white"
                                    />
                                    <path
                                        d="M1.71582 3.54416C1.36669 3.0065 0.608587 2.7775 0.359211 2.99654C0.0400085 3.28528 -0.199393 5.17704 0.23951 5.13722C0.439011 5.1173 1.9253 3.87272 1.71582 3.54416Z"
                                        fill="white"
                                    />
                                </svg>
                                <!-- <svg  viewBox="0 0 52 7" fill="none" xmlns="http://www.w3.org/2000/svg" class="absolute top-[.2rem] left-0 w-full px-3">
                                    <path d="M51.2918 0H0C0 0 4.80798 8.75313 18.4938 6.41033C32.1796 4.06752 38.9626 0.618103 51.2918 0Z" :fill="camelHightlightColors[camel.color]"/>
                                </svg> -->
                                <h1
                                    class="font-title absolute top-1 left-0 line-clamp-1 w-full text-center text-xs font-black text-ellipsis px-2 lg:px-4 lg:text-lg"
                                >
                                    {{ camel.profile.tag_name }}
                                </h1>
                            </div>

                            <CamelraceCard class="h-10 lg:h-14" :color="colors[camel.color]" smallBorders>
                                <CamelStepInput
                                    :darts-count="camelDartsThisTurn[camel.teamId] ?? 0"
                                    :is-current-players-turn="camel.isCurrentPlayersTurn"
                                    :clickable="isCamelClickable(camel)"
                                    :is-submitting="isSubmitting"
                                    :color-dark="colorsDark[camel.color]"
                                />
                            </CamelraceCard>
                        </div>
                    </div>
                </div>
            </div>
            <div class="grid grid-cols-2 grid-rows-1 gap-3" v-else>
                <div class="flex" v-for="camel in camels" :key="camel.color">
                    <div
                        class="flex w-full flex-col items-center gap-px rounded-xl p-1 relative"
                        :style="{
                            backgroundColor: colors[camel.color],
                            borderColor: colorsDark[camel.color],
                        }"
                        :class="camel.isCurrentPlayersTurn ? 'border-6 shadow-[0px_0px_12.92px_0px_#000000BA]' : 'border-4'"
                    >
                        <svg viewBox="0 0 149 16" fill="none" xmlns="http://www.w3.org/2000/svg" class="absolute top-0 w-full px-1 left-0 z-1 lg:max-h-7">
                            <path d="M148.123 0H0C0 0 100.889 18.0829 114.65 15.622C128.411 13.1611 135.724 0.649038 148.123 0Z" :fill="camelHightlightColors[camel.color]"/>
                        </svg>
                        <svg viewBox="0 0 6 6" fill="none" xmlns="http://www.w3.org/2000/svg" class="absolute top-0.5 left-0.5 w-2">
                            <path d="M5.8969 0.35866C6.00511 -0.245307 3.43509 0.0341405 2.83091 0.322602C0.829002 1.25109 1.57746 2.46803 2.19968 2.73847C2.47922 2.86467 5.77967 0.97164 5.8969 0.35866Z" fill="white"/>
                            <path d="M1.72205 3.72392C1.37037 3.16503 0.603869 2.92164 0.360393 3.147C0.0357594 3.44448 -0.198699 5.44568 0.243164 5.40061C0.441552 5.37356 1.93847 4.06647 1.72205 3.72392Z" fill="white"/>
                        </svg>
                        <h2 class="font-title text-[0.6rem] font-bold uppercase z-2 lg:text-xl">{{ $t('camelrace.team_label', { color: $t('camelrace.team_colors.' + camel.color) }) }}</h2>
                        <div
                            class="grid w-full grid-cols-[1.2rem_1fr_1.2rem] items-center lg:grid-cols-1[2.5rem_1fr_2.5rem]"
                            v-for="player in camel.players"
                            :key="player.id"
                        >
                            <img
                                :src="turnIndicators[camel.color]"
                                class="h-full w-full -rotate-90 object-contain animate-bounce translate-x-1"
                                :class="{ 'opacity-0': player.id !== currentPlayer?.id }"
                            />
                            <CamelraceCard
                                :color="colors[camel.color]"
                                smallBorders
                                class="h-7 w-full lg:h-12"
                            >
                                <div class="flex items-center gap-1 p-1 lg:p-2 lg:gap-2">
                                    <img
                                        v-avatar-fallback="player.avatar"
                                        :alt="player.tag_name"
                                        class="h-5 w-5 lg:h-8 lg:w-8 rounded object-cover object-top"
                                        :style="{ backgroundColor: colors[camel.color] }"
                                    />
                                    <span class="font-title line-clamp-1 text-[0.6rem] font-black text-ellipsis pr-2 lg:text-lg">
                                        {{ player.tag_name }}
                                    </span>
                                </div>
                            </CamelraceCard>
                            <img
                                :src="turnIndicators[camel.color]"
                                class="h-full w-full rotate-90 object-contain animate-bounce -translate-x-1"
                                :class="{ 'opacity-0': player.id !== currentPlayer?.id }"
                            />
                        </div>

                        <CamelraceCard class="h-7 w-15 lg:h-11 lg:w-25" :color="colors[camel.color]" smallBorders>
                            <CamelStepInput
                                :darts-count="camelDartsThisTurn[camel.teamId] ?? 0"
                                :is-current-players-turn="camel.isCurrentPlayersTurn"
                                :clickable="isCamelClickable(camel)"
                                :is-submitting="isSubmitting"
                                :color-dark="colorsDark[camel.color]"
                                :small="true"
                                @add="handleCamelClick(camel)"
                                @subtract="handleCamelRightClick(camel)"
                            />
                        </CamelraceCard>
                    </div>
                </div>
            </div>
            <div class="flex gap-1" v-if="currentCamel">
                <CamelraceCard
                    class="flex h-12 w-12 lg:h-18 lg:w-18"
                    @click="resetCamelState"
                    :color="colors[currentCamel.color]"
                >
                    <div class="flex h-full w-full items-center justify-center p-3 lg:p-5">
                        <svg
                            viewBox="0 0 20 20"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <g clip-path="url(#clip0_26321_35028)">
                                <g opacity="0.78" style="mix-blend-mode: overlay">
                                    <path
                                        d="M11.388 0.511719C7.37243 0.511719 4.01597 3.30467 3.06194 7.08441C2.97521 7.03154 2.89715 6.98749 2.81909 6.92582C2.3681 6.59101 1.9171 6.2474 1.4661 5.9126C1.2753 5.77163 1.04112 5.62185 0.815625 5.69234C0.607473 5.76282 0.503396 5.98308 0.451358 6.19454C0.364628 6.52053 0.338609 6.88176 0.347282 7.243C0.364628 7.8333 0.48605 8.45004 0.581454 8.98749C0.885009 10.5998 1.4661 12.1681 2.27269 13.5954C2.57625 14.1241 2.81909 14.7408 3.2354 15.1813C3.86853 15.8421 4.54503 15.6395 5.23887 15.1637C6.07148 14.591 6.94745 13.8157 7.46784 12.9346C7.94485 12.1505 8.51727 11.4721 8.9249 10.6263C9.20244 10.0448 9.48865 9.45445 9.62742 8.82009C9.67946 8.59101 9.71415 8.34432 9.66211 8.10643C9.61007 7.87736 9.44529 7.65709 9.21979 7.59542C9.09837 7.56018 8.97694 7.5778 8.85552 7.59542C8.24841 7.65709 7.66732 7.69233 7.07755 7.70115C7.71068 5.89498 9.40192 4.60863 11.3967 4.60863C13.9292 4.60863 15.9847 6.69674 15.9847 9.26943C15.9847 11.8421 13.9292 13.9302 11.3967 13.9302C10.9891 13.9302 10.5728 13.8774 10.1825 13.754C9.90496 13.6747 9.62742 13.5161 9.34121 13.5337C8.7341 13.5602 8.2831 14.1593 7.89282 14.5558C7.26836 15.1901 6.47911 15.7364 7.16428 16.6703C7.98822 17.7893 10.1391 18.0095 11.4054 18.0095C16.1582 18.0095 20.009 14.0976 20.009 9.26943C20.009 4.44123 16.1409 0.511719 11.388 0.511719Z"
                                        fill="white"
                                    />
                                </g>
                                <path
                                    d="M11.049 0C7.04211 0 3.67697 2.79295 2.72294 6.57269C2.63621 6.51982 2.55815 6.47577 2.4801 6.4141C2.0291 6.07929 1.5781 5.73568 1.1271 5.40088C0.936298 5.25991 0.702126 5.11013 0.476627 5.18062C0.268475 5.2511 0.164398 5.47136 0.11236 5.68282C0.016957 6.01762 -0.00906206 6.37004 -0.000389032 6.73128C0.016957 7.32159 0.138379 7.93833 0.233783 8.47577C0.537339 10.0881 1.11843 11.6564 1.92502 13.0837C2.22858 13.6123 2.47142 14.2291 2.88773 14.6696C3.52086 15.3304 4.19736 15.1278 4.8912 14.652C5.72381 14.0793 6.59978 13.304 7.12017 12.4229C7.59718 11.6388 8.1696 10.9604 8.57723 10.1145C8.85477 9.53304 9.14098 8.94273 9.27975 8.30837C9.33179 8.0793 9.36648 7.8326 9.31444 7.59471C9.2624 7.36564 9.09762 7.14537 8.87212 7.0837C8.75069 7.04846 8.62927 7.06608 8.50785 7.0837C7.90074 7.14537 7.31965 7.18062 6.72988 7.18943C7.36301 5.38326 9.05425 4.09692 11.049 4.09692C13.5816 4.09692 15.6371 6.18502 15.6371 8.75771C15.6371 11.3304 13.5816 13.4185 11.049 13.4185C10.6414 13.4185 10.2251 13.3656 9.83482 13.2423C9.55729 13.163 9.27975 13.0044 8.99354 13.022C8.38643 13.0485 7.93543 13.6476 7.54514 14.0441C6.92069 14.6784 6.13144 15.2247 6.81661 16.1586C7.64055 17.2775 9.79146 17.4978 11.0577 17.4978C15.8105 17.4978 19.6614 13.5859 19.6614 8.75771C19.6614 3.92951 15.8105 0 11.049 0Z"
                                    fill="white"
                                />
                                <path
                                    d="M2.38469 13.7181C1.56943 12.2907 0.997009 10.7313 0.693453 9.11013C0.589377 8.57269 0.476627 7.96476 0.459281 7.36564C0.450608 7.00441 0.476627 6.65198 0.563358 6.31718C0.624069 6.10573 0.728145 5.87665 0.927625 5.81498C1.15312 5.74449 1.3873 5.89427 1.5781 6.03524C2.0291 6.37004 2.4801 6.71366 2.93109 7.04846C3.00915 7.11013 3.09588 7.15419 3.17394 7.20705C4.12797 3.42731 7.49311 0.634361 11.5087 0.634361C14.2407 0.634361 16.6778 1.93833 18.2563 3.95595C16.7125 1.57709 14.0673 0 11.049 0C7.04211 0 3.67697 2.79295 2.72294 6.57269C2.63621 6.51982 2.55815 6.47577 2.4801 6.4141C2.0291 6.0793 1.5781 5.73568 1.1271 5.40088C0.936298 5.25991 0.702126 5.11013 0.476627 5.18062C0.268475 5.2511 0.164398 5.47137 0.11236 5.68282C0.016957 6.01762 -0.00906206 6.37004 -0.000389032 6.73128C0.016957 7.32159 0.138379 7.93833 0.233783 8.47577C0.537339 10.0881 1.11843 11.6564 1.92502 13.0837C2.22858 13.6123 2.47142 14.2291 2.88773 14.6696C2.7056 14.3524 2.54948 14.0264 2.37602 13.7181H2.38469Z"
                                    fill="#781615"
                                />
                                <path
                                    d="M7.98735 14.6688C8.37764 14.2723 8.82863 13.6732 9.43575 13.6468C9.73063 13.6379 10.0082 13.7877 10.277 13.867C10.6673 13.9816 11.0836 14.0432 11.4913 14.0432C14.0238 14.0432 16.0793 11.9551 16.0793 9.38244C16.0793 8.21945 15.6543 7.15337 14.9605 6.33398C15.3854 7.03883 15.6283 7.85821 15.6283 8.74808C15.6283 11.3208 13.5728 13.4089 11.0403 13.4089C10.6326 13.4089 10.2163 13.356 9.82603 13.2327C9.54849 13.1534 9.27096 12.9948 8.98475 13.0124C8.37764 13.0388 7.92664 13.6379 7.53635 14.0344C6.91189 14.6688 6.12265 15.215 6.80782 16.149C6.8772 16.2459 6.97261 16.3252 7.05934 16.4045C6.79915 15.6908 7.4583 15.2062 7.98735 14.6688Z"
                                    fill="#781615"
                                />
                            </g>
                            <defs>
                                <clipPath id="clip0_26321_35028">
                                    <rect width="20" height="18" fill="white" />
                                </clipPath>
                            </defs>
                        </svg>
                    </div>
                </CamelraceCard>
                                <CamelraceCard
                    class="font-title h-12 lg:h-18 flex-1 text-center text-sm lg:text-2xl leading-12 lg:leading-17 font-bold uppercase"
                    :class="{ 'pointer-events-none opacity-50': isSubmitting }"
                    data-testid="camelrace-lock-score"
                    @click="lockScore"
                    :color="colors[currentCamel.color]"
                >
                    {{ $t('camelrace.enter_score') }}
                </CamelraceCard>
            </div>
        </div>
    </div>
    <modal
        :is-visible="showCloseModal"
        :on-confirm="quitMatch"
        :on-decline="
            () => {
                showCloseModal = false
            }
        "
        :is-loading="isQuitting"
    >
        <h2
            class="justify-start text-center font-['Termina'] text-base leading-normal font-extrabold text-white"
        >
            {{ $t('page_header.leave_match_title') }}
        </h2>
        <p
            class="mt-4 justify-start self-stretch text-center font-sans text-sm leading-normal font-normal text-white"
        >
            {{ $t('page_header.leave_match_body') }}
        </p>
    </modal>
</template>

<script>
import { useMatchStore } from '@/stores/match.js'
import { camelraceAssets } from '@/stores/camelraceAssets'
import { DART_TYPES } from '@/constants/dartTypes.js'
import CamelVideo from '@/components/games/camelrace/CamelVideo.vue'
import CamelraceCard from '@/components/games/camelrace/CamelraceCard.vue'
import CamelStepInput from '@/components/games/camelrace/CamelStepInput.vue'
import CamelTurnIndicator from '@/components/games/camelrace/CamelTurnIndicator.vue'
import redTurn from '@/assets/images/games/camelrace/red-turn.png'
import blueTurn from '@/assets/images/games/camelrace/blue-turn.png'
import greenTurn from '@/assets/images/games/camelrace/green-turn.png'
import purpleTurn from '@/assets/images/games/camelrace/purple-turn.png'
import CamelBackButton from '@/components/games/camelrace/CamelBackButton.vue'
import CamelInfoButton from '@/components/games/camelrace/CamelInfoButton.vue'
import Modal from '@/components/Modal.vue'
import introVideo from '@/assets/videos/camelrace/Intro.mp4'

export default {
    name: 'CamelraceGame',
    components: {
        CamelVideo,
        CamelraceCard,
        CamelStepInput,
        CamelTurnIndicator,
        CamelBackButton,
        CamelInfoButton,
        Modal,
    },
    emits: ['quitMatch'],
    data() {
        return {
            camelColors: ['blue', 'green', 'red', 'purple'],
            colors: {
                red: '#FF453A',
                blue: '#0EC1FF',
                green: '#1AD200',
                purple: '#C000FF',
            },
            colorsDark: {
                red: '#691D17',
                blue: '#4677B2',
                green: '#315A11',
                purple: '#610FA1',
            },
            turnIndicators: {
                red: redTurn,
                blue: blueTurn,
                green: greenTurn,
                purple: purpleTurn,
            },
            camelHexColors: {
                red: '#E50328',
                blue: '#12A9D4',
                green: '#72A911',
                purple: '#B30ED3',
            },
            camelHightlightColors: {
                red: '#FFB3AF',
                blue: '#5ad4fa',
                green: '#B6E747',
                purple: '#F09AFF',
            },
            fixedCamelSections: {
                red: 12,
                blue: 13,
                green: 2,
                purple: 8,
            },
            camelStartEnd: {
                blue: { start: { x: 41.9, y: 68.6 }, end: { x: 44.4, y: 7 } },
                green: { start: { x: 58.2, y: 68.6 }, end: { x: 55.5, y: 7 } },
                red: { start: { x: 25.6, y: 68.6 }, end: { x: 33, y: 7 } },
                purple: { start: { x: 74.3, y: 68.6 }, end: { x: 66.5, y: 7 } },
            },
            camelStepSizes: [1, 1.6, 1.5, 1.45, 1.4, 1.35, 1.3, 1.25, 1.2, 1.17, 1.13],
            camelTransforms: {
                red: { rotateX: 40, rotateZ: 3 },
                blue: { rotateX: 40 },
                green: { rotateX: 40 },
                purple: { rotateX: 50, rotateZ: -2 },
            },
            camelSteps: {},
            camelSections: {},
            camelDartsThisTurn: {},
            started: false,
            currentTurn: 0,
            isSubmitting: false,
            isQuitting: false,
            showCloseModal: false,
            loadingVideoFinished: false,
            // Colors of camels whose video clips have finished warming up.
            warmedCamels: {},
            // Latches true once the game is first ready, so the splash never
            // re-shows mid-match (e.g. if the camels list momentarily changes).
            gameReadyLatched: false,
        }
    },
    computed: {
        allCamelsWarmed() {
            const cams = this.camels
            return cams.length > 0 && cams.every((camel) => this.warmedCamels[camel.color])
        },
        isGameReady() {
            if (this.gameReadyLatched) return true
            return (
                this.camelraceAssets.isReady &&
                this.loadingVideoFinished &&
                this.allCamelsWarmed
            )
        },
        introVideoSrc() {
            const userAgent = navigator.userAgent || navigator.vendor || window.opera
            return /android/i.test(userAgent) ? `${introVideo}#t=0.1` : introVideo
        },
        matchStore() {
            return useMatchStore()
        },
        profiles() {
            return this.matchStore.getProfiles
        },
        isTeamPlay() {
            return this.matchStore.isTeamPlay
        },
        currentPlayer() {
            return this.matchStore.getCurrentPlayer
        },
        currentCamel() {
            const currentProfileId = this.currentPlayer?.id
            return this.camels.find((camel) =>
                camel.players.some((player) => player.id === currentProfileId),
            )
        },
        matchData() {
            return this.matchStore.getMatchData
        },
        camelraceAssets() {
            return camelraceAssets()
        },
        lastTurnTeamScores() {
            const sets = this.matchData?.sets || []
            const currentSet = sets[sets.length - 1]
            const currentLeg = currentSet?.legs?.[currentSet.legs.length - 1]
            const turns = currentLeg?.turns || []
            if (!turns.length) {
                console.log('[CAMEL] lastTurnTeamScores: no turns yet', {
                    sets,
                    currentSet,
                    currentLeg,
                })
                return {}
            }

            // Build cumulative scores by tracking the last known score for each team
            const scores = {}
            turns.forEach((turn) => {
                const turnScores = turn.team_scores || {}
                Object.entries(turnScores).forEach(([team, score]) => {
                    scores[team] = score
                })
            })

            console.log('[CAMEL] lastTurnTeamScores:', scores)
            return scores
        },
        teamNumbers() {
            const tn = this.matchData?.settings?.team_numbers || {}
            console.log('[CAMEL] teamNumbers:', tn)
            return tn
        },
        totalDartsThisTurn() {
            return Object.values(this.camelDartsThisTurn).reduce((sum, v) => sum + Math.abs(v), 0)
        },
        camels() {
            const colorOrder = { red: 0, blue: 1, green: 2, purple: 3 }
            const currentProfileId = this.currentPlayer?.id

            // One camel per team. Build the unique team list in the order the
            // teams first appear in the profiles array so singles keeps its
            // existing color/column assignment (one profile == one team).
            const seenTeams = new Set()
            const teamOrder = []
            this.profiles.forEach((profile) => {
                if (profile.team && !seenTeams.has(profile.team)) {
                    seenTeams.add(profile.team)
                    teamOrder.push(profile.team)
                }
            })

            const result = teamOrder.map((teamId, index) => {
                const color = this.camelColors[index]
                const players = this.profiles.filter((profile) => profile.team === teamId)
                const isCurrentPlayersTurn = players.some(
                    (player) => player.id === currentProfileId,
                )
                // Representative profile: the active thrower when it's this
                // team's turn, otherwise the first member (used for the singles
                // avatar/name display).
                const representative =
                    players.find((player) => player.id === currentProfileId) || players[0]
                const positionData = this.camelStartEnd[color] || { start: {}, end: {} }
                // Section: prefer server-assigned team number, fall back to fixed per-color value
                const section = this.teamNumbers[teamId] || this.fixedCamelSections[color] || 0
                // Steps: authoritative from server only; local pending state is for counters only
                const serverStep = this.lastTurnTeamScores[teamId] ?? 0
                return {
                    color,
                    hexColor: this.camelHexColors[color],
                    teamId,
                    players,
                    profile: representative,
                    step: serverStep,
                    section,
                    dartsThisTurn: this.camelDartsThisTurn[teamId] ?? 0,
                    isCurrentPlayersTurn,
                    start: positionData.start,
                    end: positionData.end,
                    stepSizes: this.camelStepSizes,
                    transforms: this.camelTransforms[color],
                    column: colorOrder[color] || 0,
                }
            })
            console.log(
                '[CAMEL] camels computed:',
                result.map((c) => ({
                    color: c.color,
                    step: c.step,
                    section: c.section,
                    teamId: c.teamId,
                })),
            )
            return result
        },
        dartsPerSection() {
            // Calculate darts needed for each section
            const darts = {}
            for (const [profileId, clicks] of Object.entries(this.camelDartsThisTurn)) {
                darts[profileId] = Math.ceil(Number(clicks) / 3)
            }
            return darts
        },
        totalDartsUsed() {
            // Sum all darts across all sections
            return Object.values(this.dartsPerSection).reduce((sum, dartsCount) => {
                return sum + Number(dartsCount || 0)
            }, 0)
        },
        canThrow() {
            // This is now called with a camelId context
            return true
        },
    },
    methods: {
        canAddClickToSection(teamId) {
            // Can we add one more click to this section?
            const currentClicks = Number(this.camelDartsThisTurn[teamId] ?? 0)
            const newClicks = currentClicks + 1

            // How many darts would the new click require?
            const newDartsNeeded = Math.ceil(newClicks / 3)
            const currentDartsNeeded = Math.ceil(currentClicks / 3)

            // How many NEW darts does this add?
            const dartsAdded = newDartsNeeded - currentDartsNeeded

            // Check if adding this dart would exceed our limit
            return Number(this.totalDartsUsed) + Number(dartsAdded) <= 3
        },
        onLoadingVideoEnded() {
            console.log('[CAMEL] Loading video finished')
            this.loadingVideoFinished = true
        },
        resetCamelState() {
            this.camelDartsThisTurn = {}
            this.camelSections = {}
        },
        darken(baseColor, factor) {
            const hex = baseColor.replace('#', '')
            const r = parseInt(hex.substr(0, 2), 16)
            const g = parseInt(hex.substr(2, 2), 16)
            const b = parseInt(hex.substr(4, 2), 16)

            const newR = Math.max(0, Math.floor(r * (1 - factor)))
            const newG = Math.max(0, Math.floor(g * (1 - factor)))
            const newB = Math.max(0, Math.floor(b * (1 - factor)))
            return `rgb(${newR}, ${newG}, ${newB})`
        },
        isCamelClickable(camel) {
            if(this.isSubmitting) return false
            if (camel.isCurrentPlayersTurn) return true
            const pendingSection = this.camelSections[camel.teamId] ?? 0
            return camel.step + pendingSection > 0
        },
        handleCamelClick(camel) {
            // Check if we can add a click to this specific section
            if (!this.canAddClickToSection(camel.teamId)) {
                console.log(
                    '[CAMEL] handleCamelClick: blocked — would exceed 3 total darts allowed',
                )
                return
            }

            // Clicking own team's camel = +1 step (forward); clicking another = -1 for them (backward)
            const isOwnCamel = camel.isCurrentPlayersTurn

            // Block sending a non-own camel backward when they are already at step 0
            if (!isOwnCamel && !this.isCamelClickable(camel)) {
                console.log(
                    '[CAMEL] handleCamelClick: blocked — camel is at step 0, cannot go backward',
                )
                return
            }
            const direction = isOwnCamel ? 1 : -1

            this.camelSections[camel.teamId] = (this.camelSections[camel.teamId] ?? 0) + direction
            this.camelDartsThisTurn[camel.teamId] = (this.camelDartsThisTurn[camel.teamId] ?? 0) + 1
        },
        handleCamelRightClick(camel) {
            if ((this.camelDartsThisTurn[camel.teamId] ?? 0) <= 0) {
                console.log('[CAMEL] handleCamelRightClick: nothing to undo')
                return
            }

            const isOwnCamel = camel.isCurrentPlayersTurn
            const direction = isOwnCamel ? 1 : -1

            this.camelSections[camel.teamId] = (this.camelSections[camel.teamId] ?? 0) - direction
            this.camelDartsThisTurn[camel.teamId] = (this.camelDartsThisTurn[camel.teamId] ?? 0) - 1
        },
        buildDartsFromClicks() {
            console.log(
                '[CAMEL] buildDartsFromClicks — camelSections:',
                JSON.parse(JSON.stringify(this.camelSections)),
                'camelDartsThisTurn:',
                JSON.parse(JSON.stringify(this.camelDartsThisTurn)),
            )
            const darts = []

            this.camels.forEach((camel) => {
                const clicks = Math.abs(this.camelSections[camel.teamId] ?? 0)
                const section = camel.section
                console.log(
                    `[CAMEL] buildDartsFromClicks — camel[${camel.color}] clicks=${clicks} section=${section}`,
                )
                let remaining = clicks

                while (remaining > 0) {
                    if (remaining >= 3) {
                        darts.push({ dart_type: DART_TYPES.TRIPLE, dart_number: section })
                        remaining -= 3
                    } else if (remaining === 2) {
                        darts.push({ dart_type: DART_TYPES.DOUBLE, dart_number: section })
                        remaining -= 2
                    } else {
                        darts.push({ dart_type: DART_TYPES.SINGLE, dart_number: section })
                        remaining -= 1
                    }
                }
            })

            // Pad to exactly 3 darts with misses
            while (darts.length < 3) {
                darts.push({ dart_type: DART_TYPES.MISS, dart_number: 0 })
            }

            const result = darts.slice(0, 3)
            console.log('[CAMEL] buildDartsFromClicks result:', JSON.parse(JSON.stringify(result)))
            return result
        },
        async lockScore() {
            console.log(
                '[CAMEL] lockScore called — isSubmitting:',
                this.isSubmitting,
                'currentPlayer:',
                this.currentPlayer,
            )
            if (this.isSubmitting) {
                console.log('[CAMEL] lockScore: blocked — already submitting')
                return
            }

            const profileId = this.currentPlayer?.id
            if (!profileId) {
                console.warn('[CAMEL] lockScore: no currentPlayer id')
                return
            }

            this.isSubmitting = true
            this.startMatch()

            try {
                const darts = this.buildDartsFromClicks()
                console.log('[CAMEL] lockScore: submitting darts for profileId', profileId, darts)

                for (let i = 0; i < darts.length; i++) {
                    console.log(`[CAMEL] lockScore: submitting dart ${i + 1}/3`, darts[i])
                    const response = await this.matchStore.submitDart({
                        profile_id: profileId,
                        dart_number: darts[i].dart_number,
                        dart_type: darts[i].dart_type,
                        dart_index: i + 1,
                    })
                    console.log(`[CAMEL] lockScore: dart ${i + 1} response:`, response)
                }

                console.log('[CAMEL] lockScore: all darts submitted — resetting local state')
                this.camelSections = {}
                this.camelDartsThisTurn = {}
                this.currentTurn++
            } catch (error) {
                console.error('[CAMEL] lockScore: FAILED', error)
            } finally {
                this.isSubmitting = false
            }
        },
        async quitMatch() {
            if (this.isQuitting) return

            console.log('Quitting match')
            this.isQuitting = true
            const matchId = this.matchStore.match.id

            try {
                if (matchId) {
                    await this.matchStore.cancelMatch(matchId)
                }

                this.$router.push({ name: 'home' })
            } catch (error) {
                console.error('Failed to quit match:', error)

                this.$toast(
                    this.$t('general.something_went_wrong'),
                    5000,
                    this.$t('camelrace.error_quit_match'),
                )
            } finally {
                this.isQuitting = false
            }
        },
        startMatch() {
            if (this.started) return
            this.started = true

            if (this.$refs.gates && this.$refs.gates.paused) {
                this.$refs.gates.play()
            }
        },
        onCamelWarmed(color) {
            // Spread to a new object so the computed allCamelsWarmed re-evaluates.
            this.warmedCamels = { ...this.warmedCamels, [color]: true }
        },
    },
    watch: {
        isGameReady(ready) {
            if (ready) {
                this.gameReadyLatched = true
            }
        },
        matchData: {
            deep: true,
            handler(newVal) {
                console.log(
                    '[CAMEL] matchData changed — sets:',
                    newVal?.sets?.length,
                    'currentPlayer:',
                    this.currentPlayer?.id,
                )
                const sets = newVal?.sets || []
                const currentSet = sets[sets.length - 1]
                const currentLeg = currentSet?.legs?.[currentSet.legs.length - 1]
                const turns = currentLeg?.turns || []
                const lastTurn = turns[turns.length - 1]
                console.log(
                    '[CAMEL] matchData watcher — lastTurn:',
                    lastTurn ? JSON.parse(JSON.stringify(lastTurn)) : null,
                )
                console.log('[CAMEL] matchData watcher — team_scores:', lastTurn?.team_scores)
            },
        },
        currentPlayer(newVal) {
            console.log('[CAMEL] currentPlayer changed:', newVal?.id, newVal?.tag_name)
        },
        camels(newCamels) {
            // Open gates when first camel goes above 0 steps
            if (!this.started && newCamels.some((camel) => camel.step > 0)) {
                console.log('[CAMEL] First camel crossed 0 steps — opening gates')
                this.startMatch()
            }
        },
    },
    mounted() {
        console.log(
            '[CAMEL] mounted — profiles:',
            this.profiles,
            'matchData:',
            this.matchData,
            'currentPlayer:',
            this.currentPlayer,
        )

        this.camelraceAssets.preloadAssets()
    },
}
</script>

<style>
.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.5s ease;
}
.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}
</style>
